import { DashboardLayout } from "@/projects/components/dashboard-layout";
import type { Teacher } from "@/projects/lib/store";
import {
  createTeacher,
  fetchTeachersPage,
  getApiErrorMessage,
  setTeacherActive,
  updateTeacher,
  type TeacherWithStats,
} from "@/projects/lib/teachersApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/projects/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { UserPlus, Edit, Power, Loader2 } from "lucide-react";
import { toast } from "@/projects/lib/toast";
import { cn } from "@/projects/lib/utils";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";
import {
  PasswordFields,
  canSubmitPasswords,
} from "@/projects/components/password-field";

const EMPTY_TEACHER_FORM = {
  id: "",
  teacherId: "",
  name: "",
  email: "",
  mobile: "",
  subject: "",
  username: "",
  active: true,
};

export function TeachersPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TeacherWithStats | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<TeacherWithStats | null>(
    null
  );
  const [toggling, setToggling] = useState(false);

  const pagination = useServerPagination(
    ({ page, pageSize }) => fetchTeachersPage({ page, pageSize }),
    [],
    {
      onError: error =>
        toast.error(getApiErrorMessage(error, "Failed to load teachers.")),
    }
  );
  const teachers = pagination.items;
  const loading = pagination.loading;

  return (
    <DashboardLayout
      role="admin"
      title="Teacher Management"
      breadcrumb={["Home", "Teachers"]}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="text-muted-foreground text-sm">
          {loading ? "Loading…" : `${pagination.meta.count} teachers`}
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <UserPlus className="mr-2 h-4 w-4" /> Add Teacher
        </Button>
      </div>

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Teacher</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-center">Assigned</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Reward</TableHead>
                <TableHead className="text-center">Not Proper</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading teachers…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && teachers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-muted-foreground py-16 text-center text-sm"
                  >
                    No teachers found. Add your first teacher to get started.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                teachers.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-muted-foreground text-xs">
                        {t.teacherId || t.id}
                      </div>
                    </TableCell>
                    <TableCell>{t.subject || "—"}</TableCell>
                    <TableCell className="text-xs">
                      <div>{t.email || "—"}</div>
                      <div className="text-muted-foreground">
                        {t.mobile || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      {t.assigned ?? t.total ?? 0}
                    </TableCell>
                    <TableCell className="text-royal text-center">
                      {t.pending ?? 0}
                    </TableCell>
                    <TableCell className="text-success text-center">
                      {t.reward ?? 0}
                    </TableCell>
                    <TableCell className="text-destructive text-center">
                      {t.notProper ?? 0}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                          t.active
                            ? "border-success/30 bg-success/10 text-success"
                            : "border-muted-foreground/30 bg-muted text-muted-foreground"
                        )}
                      >
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            t.active ? "bg-success" : "bg-muted-foreground"
                          )}
                        />
                        {t.active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(t);
                            setOpen(true);
                          }}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmToggle(t)}
                        >
                          <Power className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {!loading && teachers.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="teachers" />
        )}
      </div>

      <TeacherFormDialog
        open={open}
        onOpenChange={setOpen}
        teacher={editing}
        onSaved={() => pagination.reload()}
      />

      <AlertDialog
        open={!!confirmToggle}
        onOpenChange={o => !o && setConfirmToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmToggle?.active ? "Deactivate" : "Activate"} teacher?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmToggle?.active
                ? `${confirmToggle?.name} will no longer be able to log in or receive new assignments.`
                : `${confirmToggle?.name} will regain access to the teacher dashboard.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={toggling}
              onClick={async e => {
                e.preventDefault();
                if (!confirmToggle) {
                  return;
                }
                setToggling(true);
                try {
                  await setTeacherActive(
                    confirmToggle.id,
                    !confirmToggle.active
                  );
                  toast.success(
                    `Teacher ${confirmToggle.active ? "deactivated" : "activated"}`
                  );
                  setConfirmToggle(null);
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "Failed to update teacher status."
                    )
                  );
                } finally {
                  setToggling(false);
                }
              }}
            >
              {toggling ? "Saving…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function TeacherFormDialog({
  open,
  onOpenChange,
  teacher,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  teacher: Teacher | null;
  onSaved: () => void;
}) {
  const [f, setF] = useState(EMPTY_TEACHER_FORM);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setF(
      teacher
        ? {
            id: teacher.id,
            teacherId: teacher.teacherId ?? "",
            name: teacher.name,
            email: teacher.email,
            mobile: teacher.mobile,
            subject: teacher.subject,
            username: teacher.username,
            active: teacher.active,
          }
        : EMPTY_TEACHER_FORM
    );
    setPassword("");
    setConfirm("");
    setShowPasswordErrors(false);
    setFormError(null);
  }, [open, teacher]);

  const personalContext = {
    username: f.username,
    email: f.email,
    phone: f.mobile,
    name: f.name,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const teacherCode = (f.teacherId || f.id).trim();
    if (
      !f.name.trim() ||
      !teacherCode ||
      !f.email.trim() ||
      !f.subject.trim() ||
      !f.username.trim()
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const wantsPasswordChange = Boolean(password || confirm);

    // Add: password required. Edit: only validate if admin is changing it.
    if (!teacher || wantsPasswordChange) {
      setShowPasswordErrors(true);
      const check = canSubmitPasswords(password, confirm, personalContext);
      if (!check.ok) {
        toast.error(check.message);
        return;
      }
    }

    setSaving(true);
    try {
      if (teacher) {
        const payload: Parameters<typeof updateTeacher>[1] = {
          teacher_name: f.name.trim(),
          teacher_id: teacherCode,
          email: f.email.trim(),
          mobile: f.mobile.trim() || undefined,
          subject_department: f.subject.trim(),
          username: f.username.trim(),
        };
        if (wantsPasswordChange) {
          payload.password = password;
          payload.confirm_password = confirm;
        }
        await updateTeacher(teacher.id, payload);
        toast.success(
          wantsPasswordChange
            ? "Teacher and password updated"
            : "Teacher updated"
        );
      } else {
        await createTeacher({
          teacher_name: f.name.trim(),
          teacher_id: teacherCode,
          email: f.email.trim(),
          mobile: f.mobile.trim() || undefined,
          subject_department: f.subject.trim(),
          username: f.username.trim(),
          password,
          confirm_password: confirm,
        });
        toast.success("Teacher added successfully");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      // Password update failures: always show a clear static front-end message.
      if (teacher && wantsPasswordChange) {
        const message =
          "Failed to update password. Please check the new password and try again.";
        setFormError(message);
        toast.error(message);
      } else {
        const message = getApiErrorMessage(
          error,
          teacher ? "Failed to update teacher." : "Failed to add teacher."
        );
        setFormError(message);
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{teacher ? "Edit Teacher" : "Add Teacher"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={e => void handleSubmit(e)}
          className="grid gap-3 sm:grid-cols-2"
        >
          <Field label="Teacher Name *">
            <Input
              value={f.name}
              onChange={e => setF({ ...f, name: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Teacher ID *">
            <Input
              value={f.teacherId || f.id}
              onChange={e => setF({ ...f, teacherId: e.target.value })}
              disabled={!!teacher || saving}
            />
          </Field>
          <Field label="Email *">
            <Input
              type="email"
              value={f.email}
              onChange={e => setF({ ...f, email: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Mobile">
            <Input
              value={f.mobile}
              onChange={e => setF({ ...f, mobile: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Subject / Department *">
            <Input
              value={f.subject}
              onChange={e => setF({ ...f, subject: e.target.value })}
              disabled={saving}
            />
          </Field>
          <Field label="Username *">
            <Input
              value={f.username}
              onChange={e => setF({ ...f, username: e.target.value })}
              disabled={saving}
            />
          </Field>
          <div className="sm:col-span-2 space-y-1">
            {teacher && (
              <p className="text-muted-foreground text-xs">
                Leave password blank to keep the current password. Fill both
                fields only if you want to change it.
              </p>
            )}
            <PasswordFields
              password={password}
              confirmPassword={confirm}
              onPasswordChange={value => {
                setPassword(value);
                setFormError(null);
              }}
              onConfirmChange={value => {
                setConfirm(value);
                setFormError(null);
              }}
              passwordLabel={teacher ? "New Password" : "Password *"}
              confirmLabel={
                teacher ? "Confirm New Password" : "Confirm Password *"
              }
              disabled={saving}
              stacked={false}
              showErrors={showPasswordErrors}
              personalContext={personalContext}
            />
            {formError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                {formError}
              </div>
            )}
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {teacher ? "Save Changes" : "Add Teacher"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
